import { useEffect, useRef, useState } from 'react'
import { CustomerContext } from '@typesApp/Customer'
import { useDebouncedCallback } from '@hooks/useDebouncedCallback'
import { useSessionInfo } from '@hooks/useSessionInfo'
import { shouldInitAuthToken } from '@utils/customerContextUtil'
import { useCustomerSession } from './useCustomerSession'

export const useAuthTokenInit = () => {
  const isReloadingRef = useRef(false)
  const { initCustomerSession } = useCustomerSession()

  // NOTE: auth token init has dependency on customer context from commerce.
  // Local state is used to prevent multiple calls to auth token init (not disastrous, just wasteful).
  // This behaviour happens on N1, where it sends cartId at a later time,
  // resulting in subsequent redux state change which re-triggers the effect in this hook.
  const [inProgress, setInProgress] = useState(false)

  const {
    customerContext,
    authToken,
    isTokenReady,
    isTokenError,
    isSessionInitializing,
    isLoggingOut,
    isSessionExpired,
  } = useSessionInfo()

  const debouncedHandler = useDebouncedCallback((context: CustomerContext) => {
    setInProgress(true)
    initCustomerSession(context, () => setInProgress(false))
  }, 100)

  const shouldInit =
    !isReloadingRef.current &&
    shouldInitAuthToken({
      inProgress: isSessionInitializing || inProgress,
      isLoggingOut,
      isSessionExpired,
      isTokenError,
      isTokenReady,
      authToken,
      customerContext,
    })

  // init auth token and active data
  useEffect(() => {
    if (shouldInit) {
      debouncedHandler(customerContext)
      return () => debouncedHandler.cancel()
    }
  }, [shouldInit, customerContext, debouncedHandler])

  // prevent token init and cancel debounce on page unload
  useEffect(() => {
    const onUnload = () => {
      debouncedHandler.cancel()
      isReloadingRef.current = true
    }

    window.addEventListener('beforeunload', onUnload)
    return () => {
      window.removeEventListener('beforeunload', onUnload)
    }
  }, [debouncedHandler])
}
